import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
var _excluded = ["userName"];
import _regeneratorRuntime from "@babel/runtime/regenerator";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { createContext, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { updateRegionBasedAPIEndpoint } from 'flow/actions/app-actions';
import wrappedLocalStorage from 'flow/utils/local-storage-utils';
import { isBetaOrDevEnvironment, isEmbeddedTypeCti, regionCheckEndpoint, regionLookupEnabled } from 'flow/utils/app-utils';
import { getElectronAPI } from 'flow/utils/electron-api';
import isElectron from 'is-electron';
import { regions } from 'flow/constants/region-lookup/login-data';
import { apiUrl } from 'app-config';
import { useQuery } from '@tanstack/react-query';
import { fetchAllRegions, fetchFeatureFlagsFromGeonex, fetchRegionDataEndpoint, isAllowedUrl, isRegionSelectorEnabled, validateRegionData } from 'flow/utils/geonex-api-utils';
import { IS_UAE_HOST } from 'flow/utils/browser-utils-TW';
import { jsx as _jsx } from "react/jsx-runtime";
export var UserRegionContext = /*#__PURE__*/createContext(null);
var STALE_TIME = 5 * 60 * 1000; // 5 minutes
var FEATURE_FLAG_STALE_TIME = 60 * 1000; // matches Geonex's Cache-Control: max-age=60

/**
 * Static prerequisites for region check — all read from runtime config at
 * import time, so they cannot change between renders.
 */
var REGION_CHECK_CONFIGURED = !IS_UAE_HOST && !!regionCheckEndpoint && regionLookupEnabled;

/**
 * Whether boot has to wait for region state to settle. Only Electron does:
 * Main dispatches prepareInitialState exactly once off isRegionInitialized and
 * picks the session endpoint from it, and Electron is the one platform we can
 * recognise synchronously. CTI only learns it is CTI once EmbedApplication
 * reports the embed type, which is after this provider's first render, so it
 * has never gated boot — and an ordinary browser session never can, so it must
 * not start waiting on a Geonex round-trip to find that out.
 */
var REGION_CHECK_GATES_BOOT = REGION_CHECK_CONFIGURED && isElectron();

/**
 * Which platforms region check applies to, unchanged from before the Geonex
 * feature flag existed: CTI on beta/dev, or Electron. The flag is layered on
 * top of this as an extra gate, never as a replacement — so only sessions that
 * were already eligible ask Geonex for it.
 *
 * Enables Region Lookup temporarily for CTI logins in beta and development
 * environments for testing purposes. isBetaOrDevEnvironment() check will be
 * removed after testing.
 */
var isRegionCheckPlatform = function isRegionCheckPlatform(isCtiEmbedded) {
  return REGION_CHECK_CONFIGURED && (isCtiEmbedded && isBetaOrDevEnvironment() || isElectron());
};
var UserRegionProvider = function UserRegionProvider(_ref) {
  var children = _ref.children;
  var _useState = useState(),
    _useState2 = _slicedToArray(_useState, 2),
    regionData = _useState2[0],
    setRegionData = _useState2[1];
  var regionDataRef = useRef(regionData);
  regionDataRef.current = regionData;
  var _useState3 = useState(null),
    _useState4 = _slicedToArray(_useState3, 2),
    electronAPIInstance = _useState4[0],
    setElectronAPIInstance = _useState4[1];
  var _useState5 = useState(null),
    _useState6 = _slicedToArray(_useState5, 2),
    userNameToFetch = _useState6[0],
    setUserNameToFetch = _useState6[1];
  var _useState7 = useState(false),
    _useState8 = _slicedToArray(_useState7, 2),
    isEndpointUpdated = _useState8[0],
    setIsEndpointUpdated = _useState8[1];
  var _useState9 = useState(null),
    _useState0 = _slicedToArray(_useState9, 2),
    embedType = _useState0[0],
    setEmbedType = _useState0[1];
  var appliedUrlRef = useRef(null);
  var isCtiEmbedded = embedType ? isEmbeddedTypeCti(embedType) : false;

  /**
   * Geonex publishes the pre-login feature flags every client reads before
   * authenticating. Asked for only by the platforms that can act on the
   * answer — on Electron that is from mount, on CTI as soon as the embed type
   * arrives — and never retried: a failed or malformed response reads as
   * "disabled", which is the safe direction.
   */
  var _useQuery = useQuery({
      queryKey: ['geonexFeatureFlags'],
      queryFn: fetchFeatureFlagsFromGeonex,
      enabled: isRegionCheckPlatform(isCtiEmbedded),
      staleTime: FEATURE_FLAG_STALE_TIME,
      gcTime: FEATURE_FLAG_STALE_TIME,
      retry: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }),
    featureFlags = _useQuery.data,
    areFeatureFlagsFetched = _useQuery.isFetched;

  /**
   * The platform rule above, plus Geonex's say-so as an extra gate.
   */
  var isRegionCheckEnabledInitial = useMemo(function () {
    return isRegionCheckPlatform(isCtiEmbedded) && isRegionSelectorEnabled(featureFlags);
  }, [isCtiEmbedded, featureFlags]);

  /**
   * Determines whether region check is enabled.
   * Region check is enabled only when the environment variable is set,
   * Geonex says the region selector is on, and the application is running
   * in CTI embedded or Electron mode.
   *
   * Where boot waits on this (see REGION_CHECK_GATES_BOOT), it must not start
   * before the flags have settled: Main dispatches prepareInitialState
   * exactly once off isRegionInitialized, and uses isRegionCheckEnabled to
   * pick the initial session endpoint.
   */
  var _useState1 = useState(!REGION_CHECK_GATES_BOOT),
    _useState10 = _slicedToArray(_useState1, 2),
    isRegionInitialized = _useState10[0],
    setIsRegionInitialized = _useState10[1];
  var _useQuery2 = useQuery({
      queryKey: ['regionData', userNameToFetch],
      queryFn: function queryFn() {
        return fetchRegionDataEndpoint(userNameToFetch);
      },
      enabled: !!userNameToFetch,
      retry: 1
    }),
    fetchedRegionData = _useQuery2.data,
    error = _useQuery2.error,
    refetch = _useQuery2.refetch;
  var _useQuery3 = useQuery({
      queryKey: ['allRegions'],
      queryFn: fetchAllRegions,
      enabled: isRegionCheckEnabledInitial,
      staleTime: STALE_TIME,
      retry: 1,
      gcTime: STALE_TIME,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false
    }),
    _useQuery3$data = _useQuery3.data,
    allRegions = _useQuery3$data === void 0 ? null : _useQuery3$data,
    errorRegions = _useQuery3.error,
    isRegionsLoading = _useQuery3.isLoading;
  var isRegionCheckEnabled = isRegionCheckEnabledInitial && !errorRegions;
  var updateRegionData = useCallback(function (data) {
    setRegionData(function (prevData) {
      return _objectSpread(_objectSpread({}, prevData), data);
    });
  }, [setRegionData]);
  useEffect(function () {
    if (fetchedRegionData && userNameToFetch) {
      if (fetchedRegionData.url && !isAllowedUrl(fetchedRegionData.url)) {
        console.error('Geonex returned a disallowed URL, ignoring response:', fetchedRegionData.url);
        return;
      }
      setRegionData(function (prevData) {
        return _objectSpread(_objectSpread(_objectSpread({}, prevData), fetchedRegionData), {}, {
          userName: userNameToFetch
        });
      });
    }
  }, [fetchedRegionData, userNameToFetch]);
  useEffect(function () {
    if (error && userNameToFetch) {
      setRegionData({
        region: regions.SWEDEN,
        url: apiUrl,
        userName: userNameToFetch,
        lastUpdatedTime: null,
        isOidcLogin: false
      });
    }
  }, [error, userNameToFetch]);
  var clearRegionData = useCallback(function () {
    setRegionData(undefined);
    setUserNameToFetch(null);
    setIsEndpointUpdated(false);
    appliedUrlRef.current = null;
    if (isElectron()) {
      electronAPIInstance === null || electronAPIInstance === void 0 || electronAPIInstance.removeUserRegionData();
    } else {
      wrappedLocalStorage.removeItem('regionData');
    }
  }, [electronAPIInstance]);
  var clearUserData = useCallback(function () {
    var _regionDataRef$curren;
    if ((_regionDataRef$curren = regionDataRef.current) !== null && _regionDataRef$curren !== void 0 && _regionDataRef$curren.socialLoginType) {
      var clearData = {
        userName: undefined,
        isOidcLogin: undefined,
        socialLoginType: undefined,
        socialSelectedUrl: undefined,
        successLoginCompleted: undefined
      };
      updateRegionData(clearData);
    } else {
      clearRegionData();
    }
  }, [updateRegionData, clearRegionData]);
  var fetchRegionData = useCallback(function (userName) {
    var isOidcLogin = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : false;
    if (userName) {
      var _regionDataRef$curren2, _regionDataRef$curren3;
      setIsEndpointUpdated(false);
      appliedUrlRef.current = ((_regionDataRef$curren2 = regionDataRef.current) === null || _regionDataRef$curren2 === void 0 ? void 0 : _regionDataRef$curren2.url) || null;
      setUserNameToFetch(userName.trim());
      updateRegionData({
        isOidcLogin: isOidcLogin,
        successLoginCompleted: false,
        lastUpdatedTime: null
      });
      if (!((_regionDataRef$curren3 = regionDataRef.current) !== null && _regionDataRef$curren3 !== void 0 && _regionDataRef$curren3.successLoginCompleted)) {
        refetch();
      }
    }
  }, [refetch, updateRegionData]);
  useEffect(function () {
    if (!isRegionCheckEnabledInitial) {
      // Where boot waits on this effect, release it only once the flags
      // have settled — unblocking earlier would let prepareInitialState
      // run against the wrong session endpoint. Everywhere else this is
      // a no-op: isRegionInitialized already started true.
      if (areFeatureFlagsFetched) {
        setIsRegionInitialized(true);
      }
      return;
    }
    if (isElectron()) {
      var electronAPI = getElectronAPI();
      setElectronAPIInstance(electronAPI);
      var getRegionDataFromElectron = /*#__PURE__*/function () {
        var _ref2 = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee() {
          var rawData, validData;
          return _regeneratorRuntime.wrap(function (_context) {
            while (1) switch (_context.prev = _context.next) {
              case 0:
                _context.next = 1;
                return electronAPI === null || electronAPI === void 0 ? void 0 : electronAPI.getUserRegionData();
              case 1:
                rawData = _context.sent;
                validData = validateRegionData(rawData);
                if (validData) {
                  setRegionData(validData);
                }
                setIsRegionInitialized(true);
              case 2:
              case "end":
                return _context.stop();
            }
          }, _callee);
        }));
        return function getRegionDataFromElectron() {
          return _ref2.apply(this, arguments);
        };
      }();
      getRegionDataFromElectron();
    } else {
      var storedRegionData = wrappedLocalStorage.getItem('regionData');
      if (storedRegionData) {
        try {
          var parsedRegionData = validateRegionData(JSON.parse(storedRegionData));
          if (parsedRegionData) {
            setRegionData(parsedRegionData);
          }
        } catch (_unused) {
          wrappedLocalStorage.removeItem('regionData');
        }
      }
      setIsRegionInitialized(true);
    }
  }, [areFeatureFlagsFetched, isRegionCheckEnabledInitial]);
  var regionUrl = regionData === null || regionData === void 0 ? void 0 : regionData.url;
  useLayoutEffect(function () {
    if (!regionUrl) return;
    if (regionUrl !== appliedUrlRef.current) {
      appliedUrlRef.current = regionUrl;
      updateRegionBasedAPIEndpoint(regionData);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionUrl]);
  useEffect(function () {
    if (!regionData) return;
    if (!regionData.url) return;
    if (regionData.url && !regionData.userName && !regionData.socialLoginType) {
      return;
    }
    var _userName = regionData.userName,
      regionDataWithoutUserName = _objectWithoutProperties(regionData, _excluded);
    if (isElectron()) {
      electronAPIInstance === null || electronAPIInstance === void 0 || electronAPIInstance.saveUserRegionData(regionDataWithoutUserName);
    } else {
      wrappedLocalStorage.setItem('regionData', JSON.stringify(regionDataWithoutUserName));
    }
    if (regionData.url && appliedUrlRef.current === regionData.url) {
      setIsEndpointUpdated(true);
    }
  }, [regionData, electronAPIInstance, clearRegionData]);
  var userRegionContextData = useMemo(function () {
    return {
      regionData: regionData,
      allRegions: allRegions,
      isRegionCheckEnabled: isRegionCheckEnabled,
      clearRegionData: clearRegionData,
      fetchRegionData: fetchRegionData,
      updateRegionData: updateRegionData,
      isEndpointUpdated: isEndpointUpdated,
      setEmbedType: setEmbedType,
      isRegionInitialized: isRegionInitialized,
      isRegionsLoading: isRegionsLoading,
      clearUserData: clearUserData
    };
  }, [regionData, allRegions, isRegionCheckEnabled, clearRegionData, fetchRegionData, updateRegionData, isEndpointUpdated, isRegionInitialized, isRegionsLoading, clearUserData]);
  return /*#__PURE__*/_jsx(UserRegionContext.Provider, {
    value: userRegionContextData,
    children: children
  });
};
export default UserRegionProvider;